import { Button, ButtonGroup, LegacyStack, Modal, Text, TextField } from '@shopify/polaris';
import { useContext, useEffect, useMemo, useState } from 'react';
import ToastContext from '../../../context/ToastContext';
import { SET_ERROR, SET_OK } from '../../../context/ToastReducer';
import UserContext from '../../../context/UserContext';
import { NEW_FILE_FAILED, NEW_FILE_LOADED, NEW_FILE_LOADING } from '../../../context/UserReducer';
import { getReportsSellsExcel } from '../../../services/reports/sellsService';

// Helper local: dispara la descarga de un blob como fichero adjunto.
// Replica el comportamiento de helpers/downloads del módulo eficacia sin
// importar de ahí para mantener el módulo de Reports independiente.
const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // El URL se revoca tras un pequeño delay para asegurarse de que el
  // click ya ha disparado la descarga (algunos navegadores son lentos).
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Compone el nombre por defecto. `eficacia_…` no encaja aquí porque
// este informe se filtra por rango libre, no por temporada. Patrón:
//   ventas_[byBarcode][byDeposit]_<since>_<until>
const buildDefaultFilename = ({ byBarcode, byDeposit, since, until }) => {
  const granularity = byBarcode ? 'barcode' : 'serie';
  const grouping = byDeposit ? '_pordepo' : '';
  const sinceStr = since ? String(since).slice(0, 10) : '';
  const untilStr = until ? String(until).slice(0, 10) : '';
  const range = sinceStr && untilStr ? `_${sinceStr}_${untilStr}` : '';
  const full = `ventas_${granularity}${grouping}${range}`;
  return full.length > 80 ? `${full.slice(0, 77)}...` : full;
};

/**
 * Modal de exportación del informe de ventas a Excel. Opciones:
 *  - Granularidad: Agrupado por serie | Detalle por barcode.
 *  - Dividir por depósitos: una fila por depósito vs. suma.
 *  - Título / subtítulo / nombre de fichero.
 *
 * Recibe `exportParams` con los filtros activos (genders, categories,
 * subcategories, since, until, deposits, customers, seasons, years).
 */
export const SellsExportModal = ({ open, onClose, exportParams }) => {
  const [, dispatchUser] = useContext(UserContext);
  const [, dispatchToast] = useContext(ToastContext);

  const [title, setTitle] = useState('Informe de Ventas');
  const [subtitle, setSubtitle] = useState('');
  const [byBarcode, setByBarcode] = useState(false);
  const [byDeposit, setByDeposit] = useState(false);
  const [withImages, setWithImages] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const defaultFilename = useMemo(
    () =>
      buildDefaultFilename({
        byBarcode,
        byDeposit,
        since: exportParams?.since,
        until: exportParams?.until,
      }),
    [byBarcode, byDeposit, exportParams?.since, exportParams?.until],
  );
  const [filename, setFilename] = useState(defaultFilename);

  // Al abrir / cambiar params, reseteamos campos a defaults.
  useEffect(() => {
    if (!open) return;
    setTitle('Informe de Ventas');
    setSubtitle('');
    setByBarcode(false);
    setByDeposit(false);
    setWithImages(true);
    setFilename(defaultFilename);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cuando el usuario cambia toggles, refrescamos el filename SI sigue
  // siendo el por defecto (no lo pisamos si lo personalizó).
  useEffect(() => {
    setFilename((current) => {
      const wasDefault =
        current ===
          buildDefaultFilename({
            byBarcode: !byBarcode,
            byDeposit,
            since: exportParams?.since,
            until: exportParams?.until,
          }) ||
        current ===
          buildDefaultFilename({
            byBarcode,
            byDeposit: !byDeposit,
            since: exportParams?.since,
            until: exportParams?.until,
          });
      return wasDefault ? defaultFilename : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byBarcode, byDeposit]);

  if (!open) return null;

  const handleExport = async () => {
    setDownloading(true);
    const fileId = `${filename}-${Date.now()}`;
    dispatchUser({
      type: NEW_FILE_LOADING,
      payload: { id: fileId, type: 'Excel ventas', name: title || filename, date: new Date() },
    });
    dispatchToast({ type: SET_OK, payload: { message: 'Generando export, puede tardar' } });
    onClose();

    try {
      const args = {
        ...exportParams,
        title,
        subtitle,
        filename,
        byBarcode: byBarcode ? 'true' : 'false',
        byDeposit: byDeposit ? 'true' : 'false',
        withImages: withImages ? 'true' : 'false',
      };
      const resp = await getReportsSellsExcel(args);
      const finalName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
      const blobUrl = URL.createObjectURL(resp.data);
      triggerBlobDownload(resp.data, finalName);
      window.open(blobUrl, '_blank', 'noopener');
      dispatchUser({ type: NEW_FILE_LOADED, payload: { id: fileId, url: blobUrl } });
      dispatchToast({ type: SET_OK, payload: { message: 'Export generado' } });
    } catch (e) {
      dispatchUser({ type: NEW_FILE_FAILED, payload: { id: fileId } });
      dispatchToast({
        type: SET_ERROR,
        payload: { message: e?.response?.data?.message || 'Error generando el export' },
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exportar — Excel Ventas"
      primaryAction={{ content: 'Exportar', onAction: handleExport, loading: downloading }}
      secondaryActions={[{ content: 'Cancelar', onAction: onClose, disabled: downloading }]}
    >
      <Modal.Section>
        <LegacyStack vertical>
          <TextField label="Nombre del fichero" value={filename} onChange={setFilename} autoComplete="off" />
          <TextField label="Título" value={title} onChange={setTitle} autoComplete="off" />
          <TextField
            label="Subtítulo"
            value={subtitle}
            onChange={setSubtitle}
            autoComplete="off"
            placeholder="(opcional)"
          />
        </LegacyStack>
      </Modal.Section>

      <Modal.Section>
        <LegacyStack vertical>
          <Text variant="bodyMd" fontWeight="semibold">
            Granularidad
          </Text>
          <ButtonGroup spacing="tight">
            <Button primary={!byBarcode} onClick={() => setByBarcode(false)}>
              Agrupado por serie-color
            </Button>
            <Button primary={byBarcode} onClick={() => setByBarcode(true)}>
              Detalle por talla
            </Button>
          </ButtonGroup>

          <Text variant="bodyMd" fontWeight="semibold">
            Comparativa por centros
          </Text>
          <ButtonGroup spacing="tight">
            <Button primary={byDeposit} onClick={() => setByDeposit((v) => !v)}>
              Dividir por depósitos
            </Button>
          </ButtonGroup>
          <Text variant="bodySm" tone="subdued">
            Si está activo, añade la columna Depósito y desglosa cada artículo por centro.
          </Text>

          <Text variant="bodyMd" fontWeight="semibold">
            Imágenes
          </Text>
          <ButtonGroup spacing="tight">
            <Button primary={withImages} onClick={() => setWithImages(true)}>
              Con foto
            </Button>
            <Button primary={!withImages} onClick={() => setWithImages(false)}>
              Sin foto
            </Button>
          </ButtonGroup>
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
};
